import React, { useState, FormEvent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';

// reCAPTCHA Site Key
const RECAPTCHA_SITE_KEY = '6LcWdjwsAAAAAPW-M99Xo81mU6DDqZXkFVixuheu';

// Declare grecaptcha global
declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    grecaptcha: any;
  }
}

const Contact: React.FC = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);

  useEffect(() => {
    // Check if reCAPTCHA script is already loaded
    const checkRecaptcha = () => {
      if (window.grecaptcha && window.grecaptcha.ready) {
        window.grecaptcha.ready(() => {
          setRecaptchaLoaded(true);
        });
      }
    };

    // Check immediately
    checkRecaptcha();

    // Also check after a short delay in case script is still loading
    const timer = setTimeout(checkRecaptcha, 1000);

    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setStatus('sending');
    setErrorMessage('');

    try {
      // Get reCAPTCHA token
      let recaptchaToken = '';
      if (recaptchaLoaded && window.grecaptcha) {
        try {
          recaptchaToken = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, {
            action: 'contact_form',
          });
        } catch (err) {
          console.warn('reCAPTCHA failed, continuing without it:', err);
        }
      }

      const response = await fetch('https://borninyu.com/send-email.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          recaptchaToken,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
        // Reset success message after 5 seconds
        setTimeout(() => setStatus('idle'), 5000);
      } else {
        setStatus('error');
        setErrorMessage(data.message || 'Failed to send message. Please try again.');
      }
    } catch {
      setStatus('error');
      setErrorMessage('Network error. Please check your connection and try again.');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <>
      <Helmet>
        <title>Contact Born in YU | Get in Touch</title>
        <meta name="description" content="Contact Born in YU for inquiries about Yugoslav nostalgia apparel, custom orders, or general questions. We're here to help." />
        <link rel="canonical" href="https://borninyu.com/contact" />
        <script
          src={`https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`}
          async
          defer
        />
      </Helmet>
      <main className="min-h-screen bg-white">
        <section className="p-6 md:p-24">
        <div className="max-w-4xl mx-auto">
          <div className="border-[3px] border-black p-8 md:p-16 relative">
            <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-white px-4 border-[3px] border-black font-black uppercase tracking-widest text-sm">
              {t('contact.classification')}
            </div>

            <h1 className="text-4xl md:text-6xl font-black uppercase tracking-tighter mb-8">
              {t('footer.links.contact')}
            </h1>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="border-2 border-black p-6">
                <div className="text-xs font-black text-gray-400 mb-2">
                  {t('contact.officialRegistry')}
                </div>
                <div className="font-typewriter text-lg uppercase">contact@borninyu.com</div>
              </div>
              <div className="border-2 border-black p-6">
                <div className="text-xs font-black text-gray-400 mb-2">
                  {t('contact.archiveLocation')}
                </div>
                <div className="font-typewriter text-lg uppercase">Yugoslavia Archives</div>
              </div>
            </div>

            {status === 'success' && (
              <div className="mb-6 border-2 border-black bg-green-50 p-4">
                <p className="text-sm font-black uppercase text-green-800">
                  Message sent successfully! We&apos;ll get back to you soon.
                </p>
              </div>
            )}

            {status === 'error' && (
              <div className="mb-6 border-2 border-black bg-red-50 p-4">
                <p className="text-sm font-black uppercase text-red-800">{errorMessage}</p>
              </div>
            )}

            <form className="space-y-6" onSubmit={handleSubmit}>
              <div className="border-2 border-black">
                <label className="block bg-gray-100 px-4 py-2 border-b-2 border-black text-xs font-black uppercase">
                  Your Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 font-typewriter text-sm uppercase outline-none"
                  placeholder="ENTER YOUR NAME"
                />
              </div>

              <div className="border-2 border-black">
                <label className="block bg-gray-100 px-4 py-2 border-b-2 border-black text-xs font-black uppercase">
                  {t('contact.subjectClassification')}
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 font-typewriter text-sm uppercase outline-none"
                  placeholder={t('contact.subjectPlaceholder')}
                />
              </div>

              <div className="border-2 border-black">
                <label className="block bg-gray-100 px-4 py-2 border-b-2 border-black text-xs font-black uppercase">
                  {t('contact.emailAddress')}
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 font-typewriter text-sm uppercase outline-none"
                  placeholder={t('contact.emailPlaceholder')}
                />
              </div>

              <div className="border-2 border-black">
                <label className="block bg-gray-100 px-4 py-2 border-b-2 border-black text-xs font-black uppercase">
                  {t('contact.messageContent')}
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="w-full px-4 py-3 font-typewriter text-sm uppercase outline-none resize-none"
                  placeholder={t('contact.messagePlaceholder')}
                />
              </div>

              <button
                type="submit"
                disabled={status === 'sending'}
                className="w-full bg-[#C41E3A] hover:bg-black text-white py-4 font-black uppercase tracking-tighter border-2 border-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {status === 'sending' ? 'SENDING...' : t('contact.submitRequest')}
              </button>

              {recaptchaLoaded && (
                <div className="mt-4 text-xs text-gray-400 text-center font-typewriter">
                  This site is protected by reCAPTCHA and the Google{' '}
                  <a
                    href="https://policies.google.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-gray-600"
                  >
                    Privacy Policy
                  </a>{' '}
                  and{' '}
                  <a
                    href="https://policies.google.com/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-gray-600"
                  >
                    Terms of Service
                  </a>{' '}
                  apply.
                </div>
              )}
            </form>
          </div>
        </div>
      </section>
    </main>
    </>
  );
};

export default Contact;
